import styled from "styled-components";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Select as BaseSelect, Option as BaseOption } from "@/app/ui/Common/StyledSelect";

import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import PopUpFilter, { generateFilter, Filter, Option } from "@/app/ui/Common/PopUpFilter";
import { useLanguage } from "@/app/utils/LanguageContext";
import { Link } from "@mui/material";
import { getCarnivalNameList } from "@/app/utils/request/requestList";

const Container = styled.div`
    width: 100%;
`;

interface propType {
    onChange: (display: string, location: string) => void;
    data: IFixture[];
    isMobile: boolean;
}

const FixtureFilter: React.FC<propType> = (props: propType) => {
    const { isMobile } = props;
    const { state: lang } = useLanguage();
    const { t } = lang;
    const defaultOption = { display: "default", location: "default" };
    const [filterSelected, setFilterSelected] = useState<{ [key: string]: string; }>(defaultOption);
    const [carnivalOrder, setCarnivalOrder] = useState<{ key: string; value: string; }[]>([]);
    const BtnGrpRef = useRef<HTMLDivElement | null>(null);

    const filterDisplayByLocation = (locationVal: string) => {
        let curD: any[] = [];
        let carnivalList: any[] = [];
        if (locationVal == "default") {
            carnivalList = props.data.map(x => x.carnivalName?.value) || [];
        } else {
            carnivalList = props.data.filter(x => (props.isMobile ? x.locationCode?.value : x.locationName?.value) == locationVal).map(x => x.carnivalName?.value) || [];
        }
        curD = [{ key: "allCarnival", value: t("allCarnival") }, ...(carnivalOrder.filter(x => Array.from(new Set(carnivalList)).includes(x?.key)))]; // follow Carnival Name folder order
        const result = generateFilter(t("display"), "display", Array.from(new Set(curD))?.map((item: { key: string; value: string; }) => { return { label: item.value, key: item.key } }), t("allRace"));
        return result;
    }

    const filterLocationByDisplay = (displayVal: string) => {
        let filteredFixtureList: any[] = [];
        switch (displayVal) {
            case "default":
                filteredFixtureList = props.data.filter(x => x.locationName?.value != "")
                break;
            case "allCarnival":
                filteredFixtureList = props.data.filter(x => x.locationName?.value != "" && x.locationName?.value != "-" && x.raceName?.value && x.carnivalName?.value?.length)
                break;
            default:
                filteredFixtureList = props.data.filter(x => x.locationName?.value != "" && x.locationName?.value != "-" && x.carnivalName?.value == displayVal)
        }
        // display locationName / locationCode according to resolution
        const locationSelectDef: any[] = Array.from(new Set([...orderByLocation(filteredFixtureList).map(x => props.isMobile ? x.locationCode?.value : x.locationName?.value).filter(x => x && x != "")]));
        const result = generateFilter(t("location"), "location", locationSelectDef.map((item: string) => { return { label: item, key: item } }), t("allLocation"));
        return result
    }

    const orderByLocation = (list: IFixture[]) => { // sort fixture list by english location name 
        if (props.isMobile) return _.orderBy(list, ["locationCode.value"], ["asc"])
        const getLatestEngName = (a: LocationNameVersion, b: LocationNameVersion) => a.version! >= b.version! && a?.language?.name?.toLowerCase() == "en-us" ? a : b;
        return _.orderBy(list, (item) => {
            return item.locationNameList?.reduce(getLatestEngName)?.locationName?.value
        }, ["asc"])
    }

    const [displaySelect, setDisplaySelect] = useState({} as Filter);
    const [locationSelect, setLocationSelect] = useState({} as Filter);

    useEffect(() => {
        setDisplaySelect(filterDisplayByLocation(filterSelected.location));
        setLocationSelect(filterLocationByDisplay(filterSelected.display));
    }, [props.data, carnivalOrder, props.isMobile]);

    useEffect(() => {
        // get carnival name list for filter dropdown
        getCarnivalNameList(lang.langCode).then((res) => {
            setCarnivalOrder(res?.item?.children?.map((x: any) => ({ key: x?.displayName, value: x?.carnivalName?.value })) || []);
        });
    }, [])

    const handleChangeDisplay = (value: any) => {
        if (value === filterSelected.display) return;
        let changeValue = value || "default";
        setFilterSelected({ ...filterSelected, display: changeValue });
        setLocationSelect(filterLocationByDisplay(changeValue));
        props.onChange(changeValue, filterSelected.location);
    };

    const handleChangeLocation = (value: any) => {
        if (value === filterSelected.location) return;
        let changeValue = value || "default";
        setFilterSelected({ ...filterSelected, location: changeValue });
        setDisplaySelect(filterDisplayByLocation(changeValue));
        props.onChange(filterSelected.display, changeValue);
    };

    const handleSelectOption = (optionKeys: { [key: string]: string; }, isTempFilter?: boolean) => {
        if (!isMobile || isMobile && !isTempFilter)
            props.onChange(optionKeys.display, optionKeys.location);
    }

    const addtionalOptionHandler = (key: string, value: string) => {
        if (key == "display") {
            setFilterSelected({ ...filterSelected, display: value });
            setLocationSelect(filterLocationByDisplay(value));
        } else {
            setFilterSelected({ ...filterSelected, location: value });
            setDisplaySelect(filterDisplayByLocation(value));
        }
    }

    const addtionalClearHandler = {
        "display": () => { setDisplaySelect(filterDisplayByLocation(defaultOption.location)) },
        "location": () => { setLocationSelect(filterLocationByDisplay(defaultOption.display)) },
    }

    return (
        <Container>
            <Box className="commHeaderTitle">
                {t("fixture_list_title")}
            </Box>
            <Grid container style={{ height: "48px", paddingRight: "8px" }} sx={{ display: { lg: "none" } }} justifyContent="space-between">
                <Grid item style={{ padding: "0 8px", textAlign: "left", alignSelf: "center" }}>
                    <Link href={`/${lang.langCode}/overseas/simulcast_calendar`} id="calendarLinkBtn">{t("racingCalendar")}</Link>
                </Grid>

                <div className="commHeaderBtnGrp" ref={BtnGrpRef}>
                    <PopUpFilter menuOption={[displaySelect, locationSelect]} selectedOption={filterSelected} handleSelectOption={handleSelectOption} addtionalOptionHandler={addtionalOptionHandler} addtionalClearHandler={addtionalClearHandler} anchorEl={BtnGrpRef} defaultOption={defaultOption} altStyle {...props}></PopUpFilter>
                </div>
            </Grid>

            {props.isMobile ?
                <></>
                : (/* Desktop only */
                    <Grid container justifyContent="space-between" display="inline-flex" direction="row" style={{ width: "100%" }}>
                        <Grid
                            container
                            item
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="center"
                            className="commFilterBox"
                            xs={10}>
                            <Grid item sx={{ marginRight: "40px" }}>
                                <span className="filterLabel">{t("display")}</span>
                                <BaseSelect
                                    className="filterSelect"
                                    id="fixture_display_select"
                                    value={filterSelected.display}
                                    onChange={(_: any, v: any) => handleChangeDisplay(v)}
                                >
                                    {displaySelect?.options?.map((item) =>
                                        <BaseOption key={item.key} value={item.key}>{item.label}</BaseOption>
                                    )}
                                </BaseSelect>
                            </Grid>

                            <Grid item>
                                <span className="filterLabel">{t("location")}</span>
                                <BaseSelect
                                    className="filterSelect"
                                    id="fixture_location_select"
                                    value={filterSelected.location}
                                    onChange={(_: any, v: any) => handleChangeLocation(v)}
                                >
                                    {locationSelect?.options?.map((item) =>
                                        <BaseOption key={item.key} value={item.key}>{item.label}</BaseOption>
                                    )}
                                </BaseSelect>
                            </Grid>

                        </Grid>

                        <Grid xs={2} item alignSelf="center" style={{ paddingRight: "17px", textAlign: "right" }}>
                            <Link href={`/${lang.langCode}/overseas/simulcast_calendar`} id="calendarLinkBtn">{t("racingCalendar")}</Link>
                        </Grid>
                    </Grid>

                )}
        </Container>
    );
};
export default FixtureFilter